/**
 * AcceptanceBySitePanel — planned-vs-actual delivery rate per pickup site.
 *
 * Phase 16 / Task 16.6 (DEC-163). Table of top-10 sites with a compact
 * progress-bar rate column and the anonymized hauler letter series column.
 */
import React from 'react';
import type { BiDateRange } from '@lodex-pro/shared/validation/disposal-bi-panels';
import { SortButton } from 'src/components/ui/sortable-head';
import { useDisposalBiAcceptanceBySite } from '../../hooks/useDisposalBiPanels';
import { ariaSort, sortRows, useUrlSort } from '../../lib/url-state';
import { PanelChrome } from './PanelChrome';
import { DateRangeToggle } from './DateRangeToggle';
import { StatCard } from './StatCard';
import { formatNumber } from '@lodex-pro/shared/number-format';

interface Props {
  readonly onModuleInactive?: () => void;
}

/**
 * The columns the Acceptance by Site table sorts by (OPEN-204); anything else in ?sort= is
 * ignored. `activity` (planned + actual) has no header: it is the API's order, the default.
 */
const SITE_SORTS = ['activity', 'site', 'series', 'planned', 'actual', 'rate'] as const;

export function AcceptanceBySitePanel({ onModuleInactive }: Props): React.JSX.Element | null {
  const [range, setRange] = React.useState<BiDateRange>('30D');
  const { data, isLoading, isError, error } = useDisposalBiAcceptanceBySite(range);
  // OPEN-204 — click a header to sort the ten busiest sites shown (which ten stays the same).
  const { sort, toggle } = useUrlSort(SITE_SORTS, { key: 'activity', dir: 'desc' }, []);

  React.useEffect(() => {
    if (isError && error !== null && error.message.includes('409')) {
      onModuleInactive?.();
    }
  }, [isError, error, onModuleInactive]);

  if (isError && error !== null && error.message.includes('409')) return null;

  const sites = data?.sites ?? [];
  const displayedSites = sortRows(sites.slice(0, 10), sort, (s, k) => {
    switch (k) {
      case 'activity':
        return s.plannedCount + s.actualCount;
      case 'site':
        return s.siteName;
      case 'series':
        return s.seriesLabel;
      case 'planned':
        return s.plannedCount;
      case 'actual':
        return s.actualCount;
      case 'rate':
        return s.rate;
    }
  });

  const overallRate =
    sites.length === 0
      ? 0
      : sites.reduce((s, x) => s + x.actualCount, 0) /
        Math.max(
          sites.reduce((s, x) => s + x.plannedCount, 0),
          1,
        );
  const belowThreshold = sites.filter((s) => s.rate < 0.8).length;

  return (
    <PanelChrome
      title="Acceptance by Site"
      description="Planned vs actual deliveries per hauler pickup site."
      actions={<DateRangeToggle value={range} onRangeChange={setRange} />}
    >
      <div className="mb-4 grid grid-cols-3 gap-3">
        <StatCard
          label="Overall Rate"
          value={`${formatNumber(overallRate * 100, { decimals: 0 })}%`}
        />
        <StatCard label="# Sites" value={String(sites.length)} />
        <StatCard label="Sites Below 80%" value={String(belowThreshold)} />
      </div>
      {isLoading ? (
        <div className="py-8 text-center text-sm text-[var(--color-text-tertiary)]">Loading…</div>
      ) : displayedSites.length === 0 ? (
        <div className="py-8 text-center text-sm text-[var(--color-text-tertiary)]">
          No deliveries in this range.
        </div>
      ) : (
        <div className="overflow-x-auto" data-testid="acceptance-by-site-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[var(--color-text-tertiary)]">
                <th className="py-2 font-medium" aria-sort={ariaSort(sort, 'site')}>
                  <SortButton column="site" sort={sort} onSort={toggle}>
                    Site
                  </SortButton>
                </th>
                <th className="py-2 font-medium" aria-sort={ariaSort(sort, 'series')}>
                  <SortButton column="series" sort={sort} onSort={toggle}>
                    Series
                  </SortButton>
                </th>
                <th className="py-2 font-medium" aria-sort={ariaSort(sort, 'planned')}>
                  <SortButton column="planned" sort={sort} onSort={toggle}>
                    Planned
                  </SortButton>
                </th>
                <th className="py-2 font-medium" aria-sort={ariaSort(sort, 'actual')}>
                  <SortButton column="actual" sort={sort} onSort={toggle}>
                    Actual
                  </SortButton>
                </th>
                <th className="py-2 font-medium" aria-sort={ariaSort(sort, 'rate')}>
                  <SortButton column="rate" sort={sort} onSort={toggle}>
                    Rate
                  </SortButton>
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedSites.map((site) => (
                <tr key={`${site.locationId}-${site.seriesKey}`} className="border-b border-border">
                  <td className="py-2 text-foreground">{site.siteName}</td>
                  <td className="py-2 text-[var(--color-text-secondary)]">{site.seriesLabel}</td>
                  <td className="py-2 text-[var(--color-text-secondary)]">
                    {formatNumber(site.plannedCount)}
                  </td>
                  <td className="py-2 text-[var(--color-text-secondary)]">
                    {formatNumber(site.actualCount)}
                  </td>
                  <td className="py-2">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-24 overflow-hidden rounded bg-secondary">
                        <div
                          className="h-full bg-primary"
                          style={{ width: `${Math.min(site.rate * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-[var(--color-text-tertiary)]">
                        {formatNumber(site.rate * 100, { decimals: 0 })}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PanelChrome>
  );
}
